import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { getRegionBySeriesId } from '@mentimeter/region';
import { addBreadcrumb } from '@mentimeter/errors/sentry';
import type { InternalStore, QRCodePlacement, SlideState } from './types';
import { getPresentationStateFromPayload } from './state-to-message';
import {
  createInitialState,
  getNextSlideState,
  getNextState,
  getNextStepState,
  getPreviousSlideState,
  getPreviousState,
  getPreviousStepState,
  getResetAllSlideStates,
  getResetStepState,
  getSeriesUpdateState,
  getSetActiveSlideState,
  getSetBlankScreenVisibleState,
  getSetQrCodePlacementState,
  getSetSlideState,
  getSetCurrentStepState,
} from './actions';
import { requestPaceData } from './request-pace-data';

// Synced over Ably
export const usePresentation = create<InternalStore>()(
  subscribeWithSelector((set, get) => {
    const setWithErrorHandling = (
      setState: (
        state: InternalStore,
      ) => InternalStore | Partial<InternalStore>,
      type: string,
    ) => {
      try {
        set((store) => {
          const state = setState(store);
          addBreadcrumb({
            category: 'usePresentation',
            message: `Setting state from action: ${type}`,
            data: {
              state: state.state ?? {},
              latestUpdate: state.latestUpdate ?? {},
            },
          });
          return { ...state, error: undefined };
        });
      } catch (error) {
        addBreadcrumb({
          type: 'error',
          level: 'error',
          category: 'usePresentation',
          message: `Setting state from action: ${type}`,
          data: get().state ?? {},
        });
        set(() => ({
          error: 'Something went wrong',
        }));
      }
    };

    return {
      state: undefined,
      internalSeries: undefined,
      latestUpdate: undefined,
      error: undefined,
      internalActions: {
        seriesUpdateHandler(series, connectionId) {
          setWithErrorHandling(({ state, latestUpdate }) => {
            if (!state) {
              return {
                internalSeries: series,
              };
            }

            return {
              state: getSeriesUpdateState(state, series),
              internalSeries: series,
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'seriesUpdate',
                placement: undefined,
                connectionId,
              },
            };
          }, 'seriesUpdate');
        },
        async revalidateSeries(id) {
          const data = await requestPaceData(id, getRegionBySeriesId(id));
          get().internalActions.seriesUpdateHandler(data, 'get-request');
        },
        setIncomingState(incomingState, incomingLatestUpdate) {
          const nextState = getPresentationStateFromPayload(incomingState);
          setWithErrorHandling(({ latestUpdate }) => {
            if (incomingLatestUpdate.timestamp === undefined) {
              throw new Error('tried to set state without timestamp');
            }
            const timestampInStore = latestUpdate?.timestamp ?? 0;
            if (incomingLatestUpdate.timestamp <= timestampInStore) {
              // Throw away incoming state since there is a newer version in the store.
              return {};
            }
            return {
              state: nextState,
              latestUpdate: incomingLatestUpdate,
            };
          }, 'setIncomingState');
        },
      },
      selectors: {
        progress() {
          const state = get().state;
          if (!state) return 0;
          return (state.slideIndex + 1) / state.totalSlides;
        },
      },
      actions: {
        async createOrUpdate(seriesId, slidePublicKey) {
          const HALF_HOUR_IN_MS = 30 * 60 * 1000;
          const latestUpdateTimestamp = get().latestUpdate?.timestamp ?? 0;
          const latestUpdateIsNewerThanHalfHour =
            latestUpdateTimestamp - Date.now() < HALF_HOUR_IN_MS;
          const { state } = get();
          if (
            state &&
            latestUpdateIsNewerThanHalfHour &&
            slidePublicKey &&
            slidePublicKey !== state?.slidePublicKey
          ) {
            /* there is a presentation, its no older than 30 minutes, and the slidePublicKey passed differs the one in state. lets update the presentation  */
            await get().internalActions.revalidateSeries(seriesId);
            get().actions.setActiveSlide(slidePublicKey);
            return;
          }
          const data = await requestPaceData(
            seriesId,
            getRegionBySeriesId(seriesId),
          );
          get().actions.create(data, slidePublicKey);
        },
        create(series, slidePublicKey) {
          setWithErrorHandling(
            () => ({
              state: createInitialState(series, slidePublicKey),
              internalSeries: series,
              latestUpdate: { type: 'createOrUpdate', placement: undefined },
            }),
            'createOrUpdate',
          );
        },
        setActiveSlide(slidePublicKey) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getSetActiveSlideState(
                state,
                internalSeries,
                slidePublicKey,
              ),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'setActiveSlide',
                connectionId: undefined,
                placement: undefined,
              },
            }),
            'setActiveSlide',
          );
        },
        resetStep(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getResetStepState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'resetStep',
                connectionId: undefined,
                placement,
              },
            }),
            'resetStep',
          );
        },
        next(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getNextState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'next',
                placement,
                connectionId: undefined,
              },
            }),
            'next',
          );
        },
        nextSlide(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getNextSlideState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'nextSlide',
                placement,
                connectionId: undefined,
              },
            }),
            'nextSlide',
          );
        },
        nextStep(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getNextStepState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'nextStep',
                placement,
                connectionId: undefined,
              },
            }),
            'nextStep',
          );
        },
        previous(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getPreviousState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'previous',
                placement,
                connectionId: undefined,
              },
            }),
            'previous',
          );
        },
        previousSlide(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getPreviousSlideState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'previousSlide',
                placement,
                connectionId: undefined,
              },
            }),
            'previousSlide',
          );
        },
        previousStep(placement) {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getPreviousStepState(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'previousStep',
                placement,
                connectionId: undefined,
              },
            }),
            'previousStep',
          );
        },
        setSlideState(publicKey, slideState) {
          setWithErrorHandling(({ state, latestUpdate, internalSeries }) => {
            const currentSlideState = state?.slideStates?.[publicKey] ?? {};
            const nextPartialSlideState =
              typeof slideState === 'function'
                ? slideState(currentSlideState)
                : slideState;
            const nextSlideState = {
              ...currentSlideState,
              ...nextPartialSlideState,
            } as SlideState;
            return {
              state: getSetSlideState(
                state,
                internalSeries,
                publicKey,
                nextSlideState,
              ),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'setSlideState',
                placement: undefined,
                connectionId: undefined,
              },
            };
          }, 'setSlideState');
        },
        resetSlideState(publicKey, placement) {
          setWithErrorHandling(({ state, latestUpdate, internalSeries }) => {
            return {
              state: getSetSlideState(state, internalSeries, publicKey, {}),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'resetSlideState',
                placement,
                connectionId: undefined,
              },
            };
          }, 'resetSlideState');
        },
        resetAllSlideStates(placement) {
          setWithErrorHandling(({ state, latestUpdate, internalSeries }) => {
            return {
              state: getResetAllSlideStates(state, internalSeries),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'resetAllSlideStates',
                placement,
                connectionId: undefined,
              },
            };
          }, 'resetAllSlideStates');
        },
        setBlankScreenVisible(visible: boolean) {
          setWithErrorHandling(({ state, latestUpdate }) => {
            return {
              state: getSetBlankScreenVisibleState(state, visible),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'setBlankScreenVisible',
                placement: undefined,
                connectionId: undefined,
              },
            };
          }, 'setBlankScreenVisible');
        },
        setQrCodePlacement(placement: QRCodePlacement) {
          setWithErrorHandling(({ state, latestUpdate }) => {
            return {
              state: getSetQrCodePlacementState(state, placement),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'setQrCodePlacement',
                placement: undefined,
                connectionId: undefined,
              },
            };
          }, 'setQrCodePlacement');
        },
        setCurrentStepState(placement, step: 'view-answers' | 'hide-answers') {
          setWithErrorHandling(
            ({ state, latestUpdate, internalSeries }) => ({
              state: getSetCurrentStepState(state, internalSeries, step),
              latestUpdate: {
                timestamp: latestUpdate?.timestamp,
                type: 'setCurrentStepState',
                placement,
                connectionId: undefined,
              },
            }),
            'setCurrentStepState',
          );
        },
      },
    };
  }),
);

export const subscribeToPresentationStore = usePresentation.subscribe;
export const getPresentationStore = usePresentation.getState;

/* return the ID of the latest presentation of this user, if it exists */
export const getLatestPresentationId = () => getPresentationStore()?.state?.id;
